import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Box, Divider, Stack, useTheme } from "@mui/material";
import { ChevronRight, type LucideIcon, X } from "lucide-react";

import {
  WorkspaceActions,
  type WorkspaceActionsClusterProps,
} from "../WorkspaceActions";
import {
  WorkspaceStatusChips,
  type WorkspaceStatusChipsProps,
} from "../WorkspaceStatusChips";

const TITLE_BAR_HEIGHT = 46;

/**
 * Explicit slice props rather than the whole view model.
 *
 * `tsconfig.options.json` sets `strictFunctionTypes`, so a view model carrying
 * property-style setters (`setSelectedTab: (tab: TabKey) => void`) is checked
 * contravariantly and `WorkspaceViewModel<PublishGateKey, CustomProviderTabKey>`
 * is NOT assignable to `WorkspaceViewModel<string, string>`. Taking a flat bag
 * keeps this component free of the key type entirely — the alternative (widening
 * by declaring the setters as methods to buy bivariance) would silently disable
 * the variance check everywhere the view model is passed.
 */
export interface TitleBarProps
  extends WorkspaceActionsClusterProps, WorkspaceStatusChipsProps {
  Icon: LucideIcon;
  breadcrumbs: readonly string[];
  /** The entity's name. */
  title: string;
  /** Shown when `title` is empty. */
  untitledLabel: string;
  onClose: () => void;
}

export const TitleBar = ({
  Icon,
  breadcrumbs,
  title,
  untitledLabel,
  statusLabel,
  activeVersion,
  hasUnsavedChanges,
  disabledActionHint,
  isSaveDisabled,
  runTests,
  isPublishDisabled,
  unpublish,
  onSaveDraft,
  onRunTests,
  onPublish,
  onUnpublish,
  onClose,
}: TitleBarProps) => {
  const theme = useTheme();

  return (
    <Box
      component="header"
      sx={{
        alignItems: "center",
        bgcolor: "background.paper",
        display: "flex",
        height: TITLE_BAR_HEIGHT,
        minHeight: TITLE_BAR_HEIGHT,
        px: 4,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ flexShrink: 0, minWidth: 0, overflow: "hidden" }}
      >
        <Icon size={16} color={theme.palette.primary.main} />
        {breadcrumbs.map((item, index) => (
          <Stack
            key={item}
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ minWidth: 0 }}
          >
            <IgTypography
              variant="body2"
              color={
                index === breadcrumbs.length - 1
                  ? theme.igPrimitives.gray["A600"]
                  : theme.igPrimitives.gray["A200"]
              }
              fontSize={11}
              fontWeight={index === breadcrumbs.length - 1 ? 500 : 400}
              lineHeight="20px"
              noWrap
            >
              {item}
            </IgTypography>
            {index < breadcrumbs.length - 1 && (
              <ChevronRight size={14} color={theme.igPrimitives.gray["A200"]} />
            )}
          </Stack>
        ))}
      </Stack>

      <Box sx={{ flex: 1, minWidth: 16 }} />

      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ flexShrink: 0, minWidth: 0 }}
      >
        <IgTypography
          variant="body1"
          fontSize={13}
          fontWeight={600}
          lineHeight="20px"
          noWrap
        >
          {title || untitledLabel}
        </IgTypography>
        <WorkspaceStatusChips
          statusLabel={statusLabel}
          activeVersion={activeVersion}
        />
        <Divider flexItem orientation="vertical" sx={{ my: 1.25 }} />
        <WorkspaceActions
          hasUnsavedChanges={hasUnsavedChanges}
          disabledActionHint={disabledActionHint}
          isSaveDisabled={isSaveDisabled}
          runTests={runTests}
          isPublishDisabled={isPublishDisabled}
          unpublish={unpublish}
          onSaveDraft={onSaveDraft}
          onRunTests={onRunTests}
          onPublish={onPublish}
          onUnpublish={onUnpublish}
        />
        <IgIconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ flexShrink: 0 }}
        >
          <X size={20} />
        </IgIconButton>
      </Stack>
    </Box>
  );
};
